"use client";

import React, { useState, useMemo } from "react";
import { Bed, Bath, Square, MapPin, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const properties = [{
  id: "prop-1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/kc_loft_interior_1.png",
  price: 1850,
  address: "4200 Main St, Apt 304",
  city: "Kansas City",
  beds: 2,
  baths: 2,
  sqft: 1150
}, {
  id: "prop-2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/liberty_suburban_home.png",
  price: 2400,
  address: "1500 Heritage Pkwy",
  city: "Liberty",
  beds: 4,
  baths: 3,
  sqft: 2200
}, {
  id: "prop-3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/nkc_modern_duplex.png",
  price: 1600,
  address: "800 Armour Rd, Unit B",
  city: "North Kansas City",
  beds: 2,
  baths: 1.5,
  sqft: 950
}, {
  id: "prop-4",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/gladstone_family_rental.png",
  price: 1950,
  address: "6500 N Oak Trfy",
  city: "Gladstone",
  beds: 3,
  baths: 2,
  sqft: 1400
}, {
  id: "prop-5",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/kc_downtown_condo.png",
  price: 2100,
  address: "1100 Walnut St, #1205",
  city: "Kansas City",
  beds: 1,
  baths: 1,
  sqft: 850
}, {
  id: "prop-6",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/clay_county_estate.png",
  price: 3200,
  address: "9000 NE 104th St",
  city: "Kansas City",
  beds: 5,
  baths: 4,
  sqft: 3100
}];
const CITIES = ["All Cities", "Kansas City", "Liberty", "North Kansas City", "Gladstone"];
const MAX_PRICE_LIMIT = 4000;
export default function FeaturedRentals() {
  const [selectedCity, setSelectedCity] = useState<string>("All Cities");
  const [maxPrice, setMaxPrice] = useState<number>(MAX_PRICE_LIMIT);
  const filteredProperties = useMemo(() => {
    return properties.filter(prop => {
      const matchesCity = selectedCity === "All Cities" || prop.city === selectedCity;
      const matchesPrice = prop.price <= maxPrice;
      return matchesCity && matchesPrice;
    });
  }, [selectedCity, maxPrice]);
  return <section id="featured-rentals" className="py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-12">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-primary">
              Available Properties
            </h2>
            <p className="text-lg text-muted-foreground">
              Browse our current selection of premium residential rentals across the Kansas City metro area. 
              Find the perfect home managed with professional care.
            </p>
          </div>

          {}
          <div className="w-full lg:w-auto flex flex-col sm:flex-row items-end gap-4 bg-card p-4 rounded-xl border border-border shadow-sm">
            <div className="w-full sm:w-48 flex flex-col gap-2">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <MapPin className="size-3" /> Location
              </label>
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="w-full bg-background">
                  <SelectValue placeholder="Select City" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((city, i) => <SelectItem key={city} value={city} data-index={i}>
                      {city}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="w-full sm:w-64 flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <SlidersHorizontal className="size-3" /> Max Price
                </label>
                <span className="text-sm font-medium text-primary">
                  ${maxPrice === MAX_PRICE_LIMIT ? `${maxPrice}+` : maxPrice}
                </span>
              </div>
              <div className="pt-2 pb-1 px-1">
                <RangeInput min={500} max={MAX_PRICE_LIMIT} step={50} value={maxPrice} onValueChange={setMaxPrice} className="slider-thumb:bg-secondary slider-thumb:size-5" />
              </div>
            </div>
          </div>
        </div>

        {}
        {filteredProperties.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((prop, i) => <Card key={prop.id} data-index={i} className="group overflow-hidden border-border/60 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col bg-card">
                {}
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  <img src={prop.image} alt={`Rental property at ${prop.address}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  
                  {}
                  <Badge className="absolute top-4 left-4 bg-primary/95 text-primary-foreground backdrop-blur-sm text-base px-3 py-1.5 shadow-lg border-none font-semibold tracking-tight">
                    ${prop.price} <span className="text-xs font-normal opacity-80 ml-1">/mo</span>
                  </Badge>

                  {}
                  <div className="absolute inset-0 bg-primary/40 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Button variant="secondary" size="lg" className="shadow-2xl transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300" asChild>
                      <Link to={`/AvailablePage#${prop.id}`}>
                        View Details
                      </Link>
                    </Button>
                  </div>
                </div>

                {}
                <CardContent className="p-6 flex flex-col flex-grow">
                  <div className="mb-4 flex-grow">
                    <h3 className="text-xl font-bold text-foreground mb-1 line-clamp-1" title={prop.address}>
                      {prop.address}
                    </h3>
                    <p className="text-muted-foreground text-sm flex items-center gap-1.5">
                      <MapPin className="size-3.5" /> {prop.city}, MO
                    </p>
                  </div>

                  {}
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-border/50">
                    <div className="flex flex-col items-center justify-center text-center gap-1 p-2 rounded-md bg-accent/50">
                      <Bed className="size-4 text-primary" />
                      <span className="text-xs font-medium text-foreground">{prop.beds} Beds</span>
                    </div>
                    <div className="flex flex-col items-center justify-center text-center gap-1 p-2 rounded-md bg-accent/50">
                      <Bath className="size-4 text-primary" />
                      <span className="text-xs font-medium text-foreground">{prop.baths} Baths</span>
                    </div>
                    <div className="flex flex-col items-center justify-center text-center gap-1 p-2 rounded-md bg-accent/50">
                      <Square className="size-4 text-primary" />
                      <span className="text-xs font-medium text-foreground">{prop.sqft} SqFt</span>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
          </div> : <div className="py-20 text-center border-2 border-dashed border-border rounded-xl bg-card/50">
            <div className="inline-flex items-center justify-center size-16 rounded-full bg-muted mb-4">
              <MapPin className="size-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">No properties found</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-6">
              We couldn't find any rentals matching your current filters. Try adjusting the city or increasing your maximum price.
            </p>
            <Button variant="outline" onClick={() => {
          setSelectedCity("All Cities");
          setMaxPrice(MAX_PRICE_LIMIT);
        }}>
              Reset Filters
            </Button>
          </div>}
      </div>
    </section>;
}